export type HistoryBadgeVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'success'
  | 'warning';

export interface HistoryEntry {
  id: string;
  created_at: string;
  actor_type: 'user' | 'system' | 'shopify';
  actor_email: string | null;
  action: string;
  summary: string;
  status: 'success' | 'failed';
  entity_type: string;
  entity_label: string | null;
  operation?: 'INSERT' | 'UPDATE' | 'DELETE' | null;
  before_json?: Record<string, unknown> | null;
  after_json?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}

export interface HistoryChange {
  field: string;
  from: string;
  to: string;
}

export interface HistoryEvent {
  id: string;
  timestamp: string;
  type:
    | 'product'
    | 'variant'
    | 'inventory'
    | 'order'
    | 'order_line_item'
    | 'refund'
    | 'customer'
    | 'activity'
    | 'error';
  title: string;
  status: {
    label: string;
    variant: HistoryBadgeVariant;
  };
  attribution: string;
  attributionTooltip?: string;
  changes?: HistoryChange[];
  summary?: string;
}

const HIDDEN_FIELDS = new Set([
  'id',
  'created_at',
  'updated_at',
  'raw_data',
  'local_edits',
  'has_local_edits',
  'last_pushed_at',
  'shopify_created_at',
  'shopify_updated_at',
  'deleted_at',
  'shopify_product_id',
  'shopify_variant_id',
  'shopify_inventory_item_id',
  'shopify_location_id',
  'shopify_order_id',
  'shopify_line_item_id',
  'shopify_refund_id',
  'shopify_customer_id',
  'customer_id',
]);

const FIELD_LABELS: Record<string, string> = {
  alt_text: 'Image alt text',
  at_nursery_price: 'Nursery price',
  available: 'Availability',
  barcode: 'Barcode',
  billing_address: 'Billing address',
  body_html: 'Description',
  cancel_reason: 'Cancellation reason',
  cancelled_at: 'Cancelled at',
  compare_at_price: 'Compare-at price',
  cost: 'Cost',
  currency: 'Currency',
  default_address: 'Default address',
  email: 'Email',
  financial_status: 'Payment status',
  first_name: 'First name',
  fulfillment_status: 'Fulfillment status',
  handle: 'Handle',
  inventory_quantity: 'Availability',
  is_connected: 'Connected',
  is_deleted: 'Shopify record',
  last_name: 'Last name',
  name: 'Name',
  note: 'Note',
  nursery_only: 'Nursery only',
  nursery_price: 'Nursery price',
  option1: 'Option 1',
  option2: 'Option 2',
  option3: 'Option 3',
  order_number: 'Order number',
  orders_count: 'Order count',
  phone: 'Phone',
  position: 'Position',
  price: 'Online price',
  processed_at: 'Processed at',
  product_type: 'Product type',
  published_at: 'Published at',
  quantity: 'Quantity',
  requires_shipping: 'Requires shipping',
  shipping_address: 'Shipping address',
  sku: 'SKU',
  state: 'State',
  status: 'Status',
  subtotal_price: 'Subtotal',
  tags: 'Tags',
  taxable: 'Taxable',
  title: 'Title',
  total_discount: 'Line discount',
  total_discounts: 'Discounts',
  total_price: 'Total',
  total_spent: 'Total spent',
  total_tax: 'Tax',
  tracked: 'Inventory tracked',
  variant_title: 'Variant title',
  vendor: 'Vendor',
};

const CURRENCY_FIELDS = new Set([
  'at_nursery_price',
  'compare_at_price',
  'cost',
  'nursery_price',
  'price',
  'subtotal_price',
  'total_discount',
  'total_discounts',
  'total_price',
  'total_spent',
  'total_tax',
]);

const DATE_FIELDS = new Set([
  'cancelled_at',
  'processed_at',
  'published_at',
]);

const ENTITY_LABELS: Record<string, string> = {
  customer: 'Customer',
  inventory: 'Availability',
  inventory_item: 'Inventory item',
  order: 'Order',
  order_line_item: 'Line item',
  product: 'Product',
  refund: 'Refund',
  variant: 'Variant',
};

export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? startCase(field);
}

export function formatHistoryValue(field: string, value: unknown): string {
  if (value === null || value === undefined) return '\u2014';

  if (field === 'is_deleted' && typeof value === 'boolean') {
    return value ? 'Deleted' : 'Active';
  }

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  if (CURRENCY_FIELDS.has(field)) {
    const amount = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(amount)) return `$${amount.toFixed(2)}`;
  }

  if (DATE_FIELDS.has(field) && typeof value === 'string') {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    }
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? value.map(String).join(', ') : 'None';
  }

  if (typeof value === 'object') {
    return formatObject(value as Record<string, unknown>);
  }

  const text = String(value);
  if (field === 'body_html') {
    return truncate(
      text
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim(),
      180,
    );
  }
  return truncate(text, 180);
}

export function toHistoryEvent(entry: HistoryEntry): HistoryEvent {
  const changes = extractChanges(entry);
  const isSoftDelete =
    entry.action.endsWith('.deleted') ||
    entry.after_json?.is_deleted === true;
  const isRestore =
    entry.action.endsWith('.restored') ||
    (entry.before_json?.is_deleted === true &&
      entry.after_json?.is_deleted === false);

  let status: HistoryEvent['status'];
  if (entry.status === 'failed') {
    status = { label: 'Failed', variant: 'destructive' };
  } else if (isSoftDelete || entry.operation === 'DELETE') {
    status = { label: 'Deleted', variant: 'destructive' };
  } else if (isRestore) {
    status = { label: 'Restored', variant: 'success' };
  } else if (entry.operation === 'INSERT') {
    status = { label: 'Created', variant: 'success' };
  } else if (entry.operation === 'UPDATE') {
    status = { label: 'Updated', variant: 'default' };
  } else {
    status = { label: 'Activity', variant: 'secondary' };
  }

  const entityLabel =
    entry.entity_label ??
    ENTITY_LABELS[entry.entity_type] ??
    startCase(entry.entity_type);
  const changedSummary = summarizeFields(changes);
  const needsEntityContext = !['product', 'order', 'customer'].includes(
    entry.entity_type,
  );

  let title: string;
  if (entry.status === 'failed') {
    title = entry.summary;
  } else if (
    entry.operation === 'UPDATE' &&
    changedSummary &&
    !isSoftDelete &&
    !isRestore
  ) {
    title = needsEntityContext
      ? `${entityLabel}: ${changedSummary}`
      : changedSummary;
  } else {
    title = entityLabel;
  }

  return {
    id: entry.id,
    timestamp: entry.created_at,
    type: historyEventType(entry),
    title,
    status,
    attribution: actorLabel(entry),
    attributionTooltip:
      entry.actor_type === 'user' && entry.actor_email
        ? entry.actor_email
        : undefined,
    changes: changes.length > 0 ? changes : undefined,
    summary: entry.summary,
  };
}

export function summarizeFields(
  changes: Pick<HistoryChange, 'field'>[],
  maxShown = 2,
): string {
  if (changes.length === 0) return '';
  const shown = changes.slice(0, maxShown).map((change) => change.field);
  const remaining = changes.length - shown.length;
  return remaining > 0
    ? `${shown.join(', ')} +${remaining} more`
    : shown.join(', ');
}

function extractChanges(entry: HistoryEntry): HistoryChange[] {
  const before = entry.before_json ?? {};
  const after = entry.after_json ?? {};
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);

  const changes = [...keys]
    .filter((key) => !HIDDEN_FIELDS.has(key))
    .filter((key) => !valuesEqual(before[key], after[key]))
    .map((key) => ({
      field: fieldLabel(key),
      from: formatHistoryValue(key, before[key]),
      to: formatHistoryValue(key, after[key]),
    }))
    // A missing value and a database NULL are technically different JSON, but
    // both display as an em dash. Do not tell users that an unchanged value
    // changed.
    .filter((change) => change.from !== change.to);

  if (changes.length > 0) return changes;

  // Preserve the useful before/after values written by the original narrow
  // triggers so pre-migration nursery, inventory, and status events still have
  // meaningful tooltips.
  if (
    entry.metadata &&
    Object.prototype.hasOwnProperty.call(entry.metadata, 'before') &&
    Object.prototype.hasOwnProperty.call(entry.metadata, 'after')
  ) {
    const legacyField = legacyFieldForAction(entry.action);
    const legacyChange = {
      field: fieldLabel(legacyField),
      from: formatHistoryValue(legacyField, entry.metadata.before),
      to: formatHistoryValue(legacyField, entry.metadata.after),
    };
    return legacyChange.from === legacyChange.to ? [] : [legacyChange];
  }

  return [];
}

function legacyFieldForAction(action: string): string {
  if (action.startsWith('nursery_price')) return 'nursery_price';
  if (action.startsWith('nursery_only')) return 'nursery_only';
  if (action.startsWith('inventory')) return 'available';
  if (action === 'product.status') return 'status';
  if (action === 'order.financial_status') return 'financial_status';
  if (action === 'order.fulfillment') return 'fulfillment_status';
  return 'value';
}

function historyEventType(entry: HistoryEntry): HistoryEvent['type'] {
  if (entry.status === 'failed') return 'error';
  if (
    entry.entity_type === 'inventory' ||
    entry.entity_type === 'inventory_item'
  ) {
    return 'inventory';
  }
  if (
    entry.entity_type === 'product' ||
    entry.entity_type === 'variant' ||
    entry.entity_type === 'order' ||
    entry.entity_type === 'order_line_item' ||
    entry.entity_type === 'refund' ||
    entry.entity_type === 'customer'
  ) {
    return entry.entity_type;
  }
  return 'activity';
}

function actorLabel(entry: HistoryEntry): string {
  if (entry.actor_type === 'shopify') return 'Shopify';
  if (entry.actor_type === 'system') return 'System';
  if (!entry.actor_email) return 'A user';
  return entry.actor_email.split('@')[0] || entry.actor_email;
}

function valuesEqual(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function formatObject(value: Record<string, unknown>): string {
  const addressParts = [
    value.name,
    value.address1,
    value.address2,
    value.city,
    value.province,
    value.zip,
    value.country,
  ].filter((part): part is string | number => Boolean(part));
  if (addressParts.length > 0) return truncate(addressParts.join(', '), 180);

  try {
    return truncate(JSON.stringify(value), 180);
  } catch {
    return '[Object]';
  }
}

function truncate(value: string, length: number): string {
  if (value.length <= length) return value || '\u2014';
  return `${value.slice(0, length - 1)}\u2026`;
}

function startCase(value: string): string {
  const spaced = value.replace(/_/g, ' ').trim();
  return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : 'Activity';
}
